import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

export default function EliminarSalidaDialog({ open, onOpenChange, salida, reservasActivas = 0, onDeleted }) {
  const [horaDestino, setHoraDestino] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setHoraDestino("");
  }, [open]);

  const eliminar = async () => {
    if (reservasActivas > 0) {
      if (!horaDestino) return toast.error("Elegí el horario al que se moverán las reservas.");
      if (horaDestino === salida?.hora) return toast.error("El horario destino tiene que ser distinto al actual.");
    }
    setBusy(true);
    try {
      const params = reservasActivas > 0 ? { hora_destino: horaDestino } : {};
      await api.delete(`/salidas/${salida.id}`, { params });
      toast.success(
        reservasActivas > 0
          ? `Salida eliminada. ${reservasActivas} reserva(s) movida(s) a las ${horaDestino}.`
          : "Salida eliminada."
      );
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="eliminar-salida-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" /> Eliminar salida
          </DialogTitle>
          <DialogDescription>
            {reservasActivas > 0 ? (
              <>
                Esta salida ({salida?.hora}) tiene <strong>{reservasActivas} reserva(s) activa(s)</strong>.
                Elegí a qué horario moverlas antes de eliminarla — no se pierden.
              </>
            ) : (
              "Esta salida no tiene reservas activas. Se eliminará permanentemente."
            )}
          </DialogDescription>
        </DialogHeader>

        {reservasActivas > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">Mover reservas al horario</Label>
            <Input
              type="time"
              value={horaDestino}
              onChange={(e) => setHoraDestino(e.target.value)}
              data-testid="eliminar-salida-hora-destino"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="eliminar-salida-cancelar">
            Cancelar
          </Button>
          <Button variant="destructive" onClick={eliminar} disabled={busy} data-testid="eliminar-salida-confirm">
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : reservasActivas > 0 ? (
              "Mover y eliminar"
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
